import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DetailNavButtons from './DetailNavButtons';
import type { DetailNavigation } from './detailNavigation';
import { useDetailNavKeys } from '../../hooks/useDetailNavKeys';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useGuruWidgetViewContext } from '../../hooks/useGuruWidgetViewContext';
/* Link import removed — customer link now opens in a new tab via <a> */
import CustomerSearch from './CustomerSearch';
import ProposalCard from './ProposalCard';
import ShipToSearch from './ShipToSearch';
import type { ShipToResult } from './ShipToSearch';
import NotificationFeedback from './NotificationFeedback';
import OrderDetectedBadge from './OrderDetectedBadge';
import AiDraftingNotice from './AiDraftingNotice';
import CreateOrderFromEmailButton from './CreateOrderFromEmailButton';
import SplitScreenModal from './SplitScreenModal';
import RoutingAlert from './RoutingAlert';
import EmailDupPoBanner from './EmailDupPoBanner';
import EmailNoDraftBanner from './EmailNoDraftBanner';
import EmailEditProposalSection from './EmailEditProposalSection';
import EmailChainThread from './EmailChainThread';
import ExtractionConcernsBanner from './ExtractionConcernsBanner';
import AttachmentList from './AttachmentList';
import AttachmentTab from './AttachmentTab';
import EmailHtmlViewer from './EmailHtmlViewer';
import EmailPrintDocument, { type EmailPrintJob } from './EmailPrintDocument';
import TrashGuardModal from './TrashGuardModal';
import FindOrderModal from './FindOrderModal';
import { emailHasLinkedOrder, resolveEmailOrderSearch } from './emailOrderSearch';
import { portalService } from '../../services/portal';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import { shouldLazyFetchBody } from '../../utils/emailBodyUtils';

/** Tab id for an attachment opened from an older message in the thread. */
const CHAIN_ATTACHMENT_TAB = '__chain_attachment__';
type InlineAttachment = { emailId: string; attachment: AttachmentMeta };
import {
  parseTrashGuardError,
  type GuardedTrashEmail,
} from '../../utils/trashGuard';
import { shouldSuppressCreateOrder } from '../../utils/emailOrderSectionUtils';
import {
  shouldFetchChain,
  getThreadBadgeCount,
  shouldShowThreadToggle,
  shouldRenderThreadView,
} from '../../utils/emailThreadUtils';
import type { AttachmentMeta, ChainEmail } from '../../types/portal';
import { useIsolatedPrint } from '../../hooks/useIsolatedPrint';
import { buildPrintableMessages, formatSender } from '../../utils/emailPrintModel';
import {
  canResolveActionItem,
  extractApiErrorMessage,
} from '../../utils/emailActionUtils';
import type { ToastType } from '../../hooks/useToast';
import styles from './EmailViewModal.module.css';
import { formatLateSyncLabel, lateSyncTitle } from '../../utils/lateSync';
import { useRecorderObject } from '../../hooks/useRecorderObject';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface EmailViewModalProps {
  email: any | null;
  /** Raw email ID from the API (UUID) for order lookup */
  emailId?: string | null;
  onClose: () => void;
  onOrderAction?: (action: string, orderId: string) => void;
  onReplyClick?: (action: 'reply' | 'reply_all' | 'forward') => void;
  /** Called when the email data changes (customer override, trash, etc.) so the parent can refetch. */
  onEmailChanged?: () => void;
  /**
   * Called when an order draft is created from this email. When provided, the
   * parent (e.g. SplitScreenModal's order pane) handles showing the new order
   * instead of this component opening its own split view — required in
   * embedded mode, where the local SplitScreenModal is never rendered.
   */
  onOrderCreated?: (emailId: string, orderId: string) => void;
  /**
   * Called when the user marks the email as unread. Opening marks an email
   * read again (2026-10-15 revert of the 09-24 rule, kanban a2888ff4), so
   * this is the one-click way back for anyone who uses unread as a reminder.
   * The host must keep it unread while the same email stays open.
   */
  onMarkUnread?: (emailId: string) => void;
  /** Explicit "Mark as read" for an email the host left unread. */
  onMarkRead?: (emailId: string) => void;
  /** Whether the email is currently unread; drives which mark button shows. Falls back to email.isUnread. */
  isUnread?: boolean;
  /**
   * Surface success/error feedback in the parent's toast container.
   * Without this, mutations in this modal fail silently.
   */
  onToast?: (message: string, type: ToastType) => void;
  /** Render mode: 'modal' (default) renders with overlay; 'embedded' renders inline for split-screen */
  renderMode?: 'modal' | 'embedded';
  /**
   * Next / previous through the inbox list the email was opened from
   * (kanban 97abd058). Header buttons plus j/k and the arrow keys; the keys
   * stay quiet while typing or while an inner overlay is open. Moving to
   * the next email marks it read the same way a list click does: the page
   * selects it, this component never touches read state itself.
   */
  navigation?: DetailNavigation;
}

/**
 * The email panel itself: header, body, attachments, sidebar, and in modal
 * mode its own overlays. Mount `EmailViewModal` (the default export) from a
 * page; it decides whether this panel stands alone or sits beside the order.
 */
export const EmailViewPanel: React.FC<EmailViewModalProps> = ({
  email,
  onMarkRead,
  isUnread,
  emailId,
  onClose,
  onOrderAction,
  onReplyClick,
  onEmailChanged,
  onOrderCreated,
  onMarkUnread,
  onToast,
  renderMode = 'modal',
  navigation,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // The recorder's leave/return markers name the email open at that moment.
  useRecorderObject({ email_id: emailId });

  // Tell the Guru feedback widget which email is in view (state-held, not in
  // the URL) so widget submissions carry the email identifiers.
  useGuruWidgetViewContext(
    'email_view',
    email || emailId
      ? {
          email_id: emailId ?? undefined,
          email_subject: (email as any)?.subject ?? undefined,
          email_from_name: (email as any)?.from ?? undefined,
          email_from_address: (email as any)?.email ?? undefined,
          email_customer_name: (email as any)?.customerName ?? (email as any)?.customer?.name ?? undefined,
        }
      : null,
  );

  const [emailBodyView, setEmailBodyView] = useState<'text' | 'html'>(
    (email as any)?.bodyHtml ? 'html' : 'text',
  );

  // Customer override state
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerOverride, setCustomerOverride] = useState<{
    name: string;
    customerNo: string;
    slug: string | null;
    salesperson: string | null;
    isManualOverride: boolean;
  } | null>(null);
  const [clearingOverride, setClearingOverride] = useState(false);

  // The email's linked order (the primary one GET /portal/orders/by-email
  // returns, drafts first). One fetch feeds three readers: the header
  // "Open order" control, the divergence badge and the proposal card's
  // draft-scoped ship-to create (kanban 0c4b9133 folded the two earlier
  // fetches of the same endpoint into this one).
  const [linkedOrder, setLinkedOrder] = useState<{
    id: string;
    customerNo: string | null;
    poNumber: string | null;
    status: string;
  } | null>(null);
  const hasLinkedOrder = emailHasLinkedOrder(email as any);

  // "Find the order" (no linked order): the Orders search opened in place.
  const [showFindOrder, setShowFindOrder] = useState(false);

  // Trash state
  const [trashing, setTrashing] = useState(false);
  // Server trash guard (409): the guarded listing for the confirm dialog.
  const [trashGuard, setTrashGuard] = useState<GuardedTrashEmail[] | null>(null);

  // Action item dismiss/resolve state
  const [isDismissed, setIsDismissed] = useState(
    !!(email as any)?.notificationIsDismissed,
  );
  const [dismissing, setDismissing] = useState(false);

  // Chain state — default to thread view (like a standard email inbox)
  const [chainEmails, setChainEmails] = useState<ChainEmail[]>([]);
  const [chainLoading, setChainLoading] = useState(false);
  const [showChain, setShowChain] = useState(true);

  // Order detail modal state (opened via "View Full Order" button)
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [orderDetailEmailId, setOrderDetailEmailId] = useState<string | null>(null);
  const [orderDetailOrderId, setOrderDetailOrderId] = useState<string | null>(null);

  // "Find the order" prefill: customer number when mapped, else the sender's
  // company (proposed customer name, domain stem, display name).
  const findOrderSearch = useMemo(
    () => resolveEmailOrderSearch(email as any, customerOverride),
    [email, customerOverride],
  );

  // A row picked in the Find-the-order results opens beside the email.
  const openFoundOrder = useCallback((orderId: string) => {
    if (!emailId) return;
    trackEvent('click', 'email_open_order', {
      email_id: emailId,
      order_id: orderId,
      source: 'find_order',
    });
    setShowFindOrder(false);
    setOrderDetailEmailId(emailId);
    setOrderDetailOrderId(orderId);
    setShowOrderDetailModal(true);
  }, [emailId]);

  // Ship-to search state
  const [showShipToSearch, setShowShipToSearch] = useState(false);
  const [selectedShipTo, setSelectedShipTo] = useState<ShipToResult | null>(null);
  const [shipToSaving, setShipToSaving] = useState(false);

  // Print menu state
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  // Embedded (split view) mode: the AI cards and order block sit under a
  // toggle so the email body is what the CSR sees first on a laptop
  // (2026-09-24 order portal review, Hadi: "it cuts off a lot of the email").
  const [embeddedExtrasOpen, setEmbeddedExtrasOpen] = useState(false);
  const { job: printJob, startPrint, handleReady: handlePrintReady } = useIsolatedPrint<EmailPrintJob>();
  const [printLoading, setPrintLoading] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);

  // Content tab state: 'email', an attachment filename of THIS email, or
  // CHAIN_ATTACHMENT_TAB for an attachment of another message in the thread.
  const [activeContentTab, setActiveContentTab] = useState<string>('email');
  // An attachment from an older message in the chain, opened inline from the
  // thread view; it gets its own tab because the tab bar lists only the head
  // email's attachments.
  const [chainAttachmentTab, setChainAttachmentTab] = useState<InlineAttachment | null>(null);

  // Beside an order (embedded pane) a previewable attachment opens in its tab,
  // next to the order fields, instead of in the full-screen overlay that hid
  // the order and had to be closed before saving (kanban fe62ebea: the order
  // desk opened and closed that overlay 27 times a week).
  const openAttachmentInline = useCallback(
    (att: AttachmentMeta, fromEmailId: string) => {
      const own: AttachmentMeta[] = (email as any)?.attachments ?? [];
      if (fromEmailId === emailId && own.some((a) => a.filename === att.filename)) {
        setChainAttachmentTab(null);
        setActiveContentTab(att.filename);
      } else {
        setChainAttachmentTab({ emailId: fromEmailId, attachment: att });
        setActiveContentTab(CHAIN_ATTACHMENT_TAB);
      }
    },
    [email, emailId],
  );

  // Lazy-loaded body content — list responses exclude bodies to reduce payload.
  const [lazyBody, setLazyBody] = useState<string | null>(null);
  const [lazyBodyHtml, setLazyBodyHtml] = useState<string | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  const emailCount = (email as any)?.emailCount ?? 1;
  const threadBadgeCount = getThreadBadgeCount(chainEmails.length, emailCount);

  // Order draft created from this email ("Create Order Draft" / "Add Another
  // Order"). When the parent supplied onOrderCreated (split-screen embed), it
  // owns showing the new order — the order pane switches to it and refreshes
  // its sibling list. Otherwise open our own split view on the new order.
  const handleOrderCreatedFromEmail = useCallback(
    (eid: string, oid: string) => {
      if (onOrderCreated) {
        onOrderCreated(eid, oid);
      } else {
        setOrderDetailEmailId(eid);
        setOrderDetailOrderId(oid);
        setShowOrderDetailModal(true);
      }
      onEmailChanged?.();
    },
    [onOrderCreated, onEmailChanged],
  );

  // Customer override handlers
  const handleCustomerOverride = useCallback(async () => {
    // Re-fetch email detail to get updated customer info
    if (!emailId) return;
    try {
      const detail = await portalService.getEmailDetail(emailId);
      if (detail?.customer) {
        setCustomerOverride({
          name: detail.customer.name || 'Unknown',
          customerNo: detail.customer.customer_no || '',
          slug: detail.customer.slug || null,
          salesperson: detail.customer.account || null,
          isManualOverride: detail.customer.is_manual_override || false,
        });
      }
    } catch {
      // Fallback: just hide search — data refreshes on next list load
    }
    setShowCustomerSearch(false);
    // Notify parent so the email list refreshes (chain propagation
    // may have updated other emails in the same thread).
    onEmailChanged?.();
  }, [emailId, onEmailChanged]);

  const handleClearOverride = useCallback(async () => {
    if (!emailId) return;
    setClearingOverride(true);
    try {
      await portalService.clearCustomerOverride(emailId);
      portalService.invalidateStatsCache();
      setCustomerOverride(null);
      onEmailChanged?.();
    } catch (err) {
      onToast?.(
        extractApiErrorMessage(err, 'Failed to clear customer override.'),
        'error',
      );
    } finally {
      setClearingOverride(false);
    }
  }, [emailId, onEmailChanged, onToast]);

  // Fetch the linked order while the email has one. Re-runs when the email
  // or its (override) customer changes, since a reassignment can re-bind the
  // order server-side.
  useEffect(() => {
    let cancelled = false;
    if (!emailId || !hasLinkedOrder) {
      setLinkedOrder(null);
      return;
    }
    (async () => {
      try {
        const detail = await portalOrderService.getOrderByEmailId(emailId);
        const order = detail?.order;
        if (cancelled) return;
        if (order?.id) {
          setLinkedOrder({
            id: order.id,
            customerNo: order.customer_no ?? null,
            poNumber: order.po_number ?? null,
            status: order.status,
          });
        } else {
          setLinkedOrder(null);
        }
      } catch {
        if (!cancelled) setLinkedOrder(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [emailId, hasLinkedOrder, email, customerOverride]);

  const emailCustomerNo: string | null =
    customerOverride?.customerNo
    || (email as any)?.customer?.customerNo
    || (email as any)?.customer?.customer_no
    || null;

  // Reassignment → order safety-valve: set when the email's linked order still
  // carries a different customer than the email itself (e.g. after choosing
  // "email only", or a divergence carried over from a prior session — the
  // PO 3946 / AU01021 RCA case). Drives the divergence badge. Null for an
  // unmapped email (no email customer to diverge from).
  const orderDivergence = useMemo<{ po: string | null; orderCustomer: string } | null>(() => {
    if (!linkedOrder || !emailCustomerNo) return null;
    if (linkedOrder.customerNo && linkedOrder.customerNo !== emailCustomerNo) {
      return { po: linkedOrder.poNumber, orderCustomer: linkedOrder.customerNo };
    }
    return null;
  }, [linkedOrder, emailCustomerNo]);

  // The email's linked EDITABLE draft order — lets the proposal card offer
  // "Create ship-to in Sage" directly from the email surface (the ship-to is
  // order/customer-scoped, so it needs the draft + a bound customer). Only
  // relevant when the email carries proposals — exactly the propose-new case.
  const linkedDraftOrder = useMemo<{ id: string; customerNo: string | null } | null>(() => {
    if (!linkedOrder || !(email as any)?.proposals) return null;
    if (linkedOrder.status === 'draft' || linkedOrder.status === 'submission_failed') {
      return { id: linkedOrder.id, customerNo: linkedOrder.customerNo };
    }
    return null;
  }, [linkedOrder, email]);

  // Trash handler — creates an IGNORE decision so the email is hidden from unmapped views
  const performTrash = useCallback(async (confirmGuarded: boolean) => {
    if (!emailId) return;
    setTrashing(true);
    try {
      await portalService.markAsTrash(emailId, { confirmGuarded });
      portalService.invalidateStatsCache();
      onToast?.('Email moved to trash.', 'success');
      onEmailChanged?.();
      onClose();
    } catch (err) {
      // Server trash guard (409): order-related / Important email — show
      // the confirm dialog instead of an error toast.
      const guarded = parseTrashGuardError(err);
      if (guarded) {
        setTrashGuard(guarded);
      } else {
        onToast?.(
          extractApiErrorMessage(err, 'Failed to move email to trash.'),
          'error',
        );
      }
    } finally {
      setTrashing(false);
    }
  }, [emailId, onEmailChanged, onClose, onToast]);

  const handleMarkAsTrash = useCallback(() => {
    void performTrash(false);
  }, [performTrash]);

  // Dismiss/undismiss handler — toggles the action item's resolved state.
  //
  // If the backend reports 404 ("No action item found for this email") the
  // action item has already gone away (notification was deleted, or the
  // decision lost its link) — we treat that as a benign success rather
  // than a hard error so the user isn't left with a red toast and a
  // button that will never work.
  const handleToggleDismiss = useCallback(async () => {
    if (!emailId) return;
    setDismissing(true);
    try {
      if (isDismissed) {
        await portalService.undismissActionItem(emailId);
        setIsDismissed(false);
        onToast?.('Action item restored.', 'success');
      } else {
        await portalService.dismissActionItem(emailId);
        setIsDismissed(true);
        onToast?.('Marked as resolved.', 'success');
      }
      portalService.invalidateStatsCache();
      onEmailChanged?.();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 && !isDismissed) {
        // Orphan decision: nothing to dismiss server-side, but locally we
        // can still hide the button and refresh so the email leaves the
        // Important tab.
        setIsDismissed(true);
        portalService.invalidateStatsCache();
        onEmailChanged?.();
        onToast?.('Marked as resolved.', 'success');
      } else {
        onToast?.(
          extractApiErrorMessage(
            err,
            isDismissed
              ? 'Failed to restore action item.'
              : 'Failed to mark as resolved.',
          ),
          'error',
        );
      }
    } finally {
      setDismissing(false);
    }
  }, [emailId, isDismissed, onEmailChanged, onToast]);

  // Handle ship-to address selection
  const handleShipToSelect = useCallback(async (address: ShipToResult) => {
    setSelectedShipTo(address);
    setShowShipToSearch(false);
    setShipToSaving(true);

    try {
      // If this email has a linked order, update the order's ship-to
      if (emailId && ((email as any)?.hasOrder || (email as any)?.orderRecordStatus)) {
        const orderDetail = await portalOrderService.getOrderByEmailId(emailId);
        if (orderDetail?.order?.id) {
          await portalOrderService.updateOrder(orderDetail.order.id, {
            ship_to_address: {
              ship_to_code: address.ship_to_code,
              ship_to_name: address.ship_to_name,
              address1: address.address1,
              address2: address.address2 || '',
              city: address.city,
              state: address.state,
              zip_code: address.zip_code,
              warehouse_code: address.warehouse_code || '',
            },
            ...(address.warehouse_code ? { warehouse_code: address.warehouse_code } : {}),
          });
        }
      }

      // Auto-populate customer if no customer is set and the address has one
      const currentCustomer = customerOverride || (email as any)?.customer;
      const hasCustomer = currentCustomer?.customerNo || currentCustomer?.customer_no;
      if (!hasCustomer && address.customer_no && emailId) {
        await portalService.overrideCustomer(emailId, address.customer_no);
        portalService.invalidateStatsCache();
        // Re-fetch to update customer display
        const detail = await portalService.getEmailDetail(emailId);
        if (detail?.customer) {
          setCustomerOverride({
            name: detail.customer.name || 'Unknown',
            customerNo: detail.customer.customer_no || '',
            slug: detail.customer.slug || null,
            salesperson: detail.customer.account || null,
            isManualOverride: detail.customer.is_manual_override || false,
          });
        }
        onEmailChanged?.();
      }
    } catch (err) {
      console.error('Failed to save ship-to address:', err);
    } finally {
      setShipToSaving(false);
    }
  }, [emailId, email, customerOverride, onEmailChanged]);

  // Handle ship-to customer mismatch — ask user to confirm
  const handleShipToMismatch = useCallback((
    address: ShipToResult,
    ownerCustomerNo: string,
    ownerCustomerName: string,
  ) => {
    const currentCustomer = customerOverride || (email as any)?.customer;
    const currentName = currentCustomer?.name || currentCustomer?.customerNo || 'current customer';
    const confirmed = window.confirm(
      `This address belongs to ${ownerCustomerName} (${ownerCustomerNo}), ` +
      `not ${currentName}. Select it anyway and switch customer?`
    );
    if (confirmed) {
      // Select the address and also switch customer
      handleShipToSelect(address);
      if (emailId && ownerCustomerNo) {
        portalService.overrideCustomer(emailId, ownerCustomerNo).then(() => {
          portalService.invalidateStatsCache();
          return portalService.getEmailDetail(emailId);
        }).then((detail) => {
          if (detail?.customer) {
            setCustomerOverride({
              name: detail.customer.name || 'Unknown',
              customerNo: detail.customer.customer_no || '',
              slug: detail.customer.slug || null,
              salesperson: detail.customer.account || null,
              isManualOverride: detail.customer.is_manual_override || false,
            });
          }
          onEmailChanged?.();
        }).catch(console.error);
      }
    }
  }, [email, customerOverride, emailId, handleShipToSelect, onEmailChanged]);

  // Print renders a dedicated document (EmailPrintDocument): the whole
  // thread with full bodies, plus the order when asked — never the modal.
  // A print clicked before the thread / full body has loaded fetches them
  // first, so paper never gets the truncated list preview.
  const loadPrintMessages = useCallback(async () => {
    const e = email as any;
    let chain = chainEmails;
    let bodyText: string | null = lazyBody;
    let bodyHtml: string | null = e?.bodyHtml || lazyBodyHtml || null;
    if (emailId && chain.length === 0) {
      try {
        chain = (await portalService.getEmailChain(emailId)).emails;
      } catch (err) {
        console.error('Failed to load thread for print:', err);
      }
      if (chain.length === 0 && bodyText === null && !bodyHtml) {
        try {
          const detail = await portalService.getEmailDetail(emailId);
          bodyText = detail?.body || null;
          bodyHtml = detail?.body_html || null;
        } catch (err) {
          console.error('Failed to load email body for print:', err);
        }
      }
    }
    return buildPrintableMessages(chain, {
      id: emailId ?? e?.id ?? 'email',
      subject: e?.subject ?? null,
      from: formatSender(e?.from, e?.email),
      to: e?.toAddress ?? null,
      cc: e?.ccAddresses ?? null,
      receivedAt: e?.receivedAt ?? null,
      timeLabel: e?.time ?? null,
      bodyText: bodyText || e?.body || e?.preview || null,
      bodyHtml,
      attachments: e?.attachments ?? [],
    });
  }, [email, emailId, chainEmails, lazyBody, lazyBodyHtml]);

  const handlePrintEmail = useCallback(async () => {
    setShowPrintMenu(false);
    setPrintLoading(true);
    const messages = await loadPrintMessages();
    setPrintLoading(false);
    startPrint({ messages });
  }, [loadPrintMessages, startPrint]);

  const handlePrintWithOrder = useCallback(async () => {
    if (!emailId) return;
    setShowPrintMenu(false);
    setPrintLoading(true);
    const [messages, orderData] = await Promise.all([
      loadPrintMessages(),
      portalOrderService.getOrderByEmailId(emailId).catch((err: unknown) => {
        // Fall back to printing the email alone.
        console.error('Failed to load order for print:', err);
        return null;
      }),
    ]);
    setPrintLoading(false);
    startPrint({
      messages,
      order: orderData ? { order: orderData.order, lineItems: orderData.line_items } : null,
    });
  }, [emailId, loadPrintMessages, startPrint]);

  const closePrintMenu = useCallback(() => setShowPrintMenu(false), []);
  useClickOutside(printMenuRef, closePrintMenu, showPrintMenu);

  // Sync isDismissed when email prop changes (e.g., navigating between emails)
  useEffect(() => {
    setIsDismissed(!!(email as any)?.notificationIsDismissed);
  }, [email]);

  // Effective body values: prefer inline data, fall back to lazy-loaded
  // lazyBody first: mapEmailToComponent fills `body` with the list preview
  // when the list response carried no body, and that preview is truncated.
  const effectiveBody = lazyBody || (email as any)?.body || email?.preview;
  const effectiveBodyHtml = (email as any)?.bodyHtml || lazyBodyHtml || null;

  // thread_dup re-sends and order_revision change-requests both surface the
  // existing order as a one-click link in EmailNoDraftBanner, so suppress the
  // redundant OrderDetected / "Create Order" section below (it would push a
  // duplicate — and for revisions the CSR should EDIT the existing order,
  // never create a second one). Suppression requires the reason to actually
  // carry an existing order — see shouldSuppressCreateOrder.
  const isThreadDup = shouldSuppressCreateOrder((email as any)?.noDraftReason);

  // Lazy-fetch body when modal opens and inline HTML body is missing.
  // See shouldLazyFetchBody for the guard logic — mapEmailToComponent sets
  // body = preview as fallback, so we check bodyHtml specifically.
  useEffect(() => {
    if (!shouldLazyFetchBody(emailId ?? null, (email as any)?.bodyHtml, lazyBodyHtml)) return;
    setBodyLoading(true);
    portalService
      .getEmailDetail(emailId!)
      .then((detail) => {
        if (detail) {
          setLazyBody(detail.body || null);
          setLazyBodyHtml(detail.body_html ?? '');
        }
      })
      .catch((err) => {
        console.error('Failed to load email body:', err);
      })
      .finally(() => setBodyLoading(false));
  }, [emailId, email, lazyBodyHtml]);

  // Reset state when switching emails
  useEffect(() => {
    const hasInlineHtml = !!(email as any)?.bodyHtml;
    setEmailBodyView(hasInlineHtml ? 'html' : 'text');
    setLazyBody(null);
    setLazyBodyHtml(null);
    setChainEmails([]);
    setShowChain(true);
    setSelectedShipTo(null);
    setShowShipToSearch(false);
    setShowPrintMenu(false);
    setActiveContentTab('email');
    setChainAttachmentTab(null);
  }, [email?.subject, email?.email]);

  // Switch to HTML view once lazy-loaded body arrives
  useEffect(() => {
    if (lazyBodyHtml) {
      setEmailBodyView('html');
    }
  }, [lazyBodyHtml]);

  // Always fetch chain for thread view — includes sent replies
  useEffect(() => {
    if (!shouldFetchChain(emailId)) return;
    setChainLoading(true);
    portalService
      .getEmailChain(emailId!)
      .then((chain) => {
        setChainEmails(chain.emails);
      })
      .catch((err) => {
        console.error('Failed to load email chain:', err);
      })
      .finally(() => {
        setChainLoading(false);
      });
  }, [emailId]);

  // Close on Escape key — skip if an inner overlay is open. An inner overlay
  // consuming the key marks it spent (preventDefault) so a host underneath
  // does not close on the same press. Embedded (split view, the order's
  // email pane) the host owns closing: this panel's onClose is the HOST's
  // close, and firing it here closed the whole order on one Escape (kanban
  // 2c097936).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return; // spent by a control that acted on it
        if (showShipToSearch) {
          e.preventDefault();
          setShowShipToSearch(false);
          return;
        }
        if (showCustomerSearch) {
          e.preventDefault();
          setShowCustomerSearch(false);
          return;
        }
        if (showOrderDetailModal) return; // OrderDetailModal handles its own Escape
        if (renderMode === 'embedded') return;
        // Macrotask: every listener has run by then (a microtask would run
        // between listeners on a real key press).
        window.setTimeout(() => { if (!e.defaultPrevented) onClose(); }, 0);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, renderMode, showShipToSearch, showCustomerSearch, showOrderDetailModal]);

  // Next / previous shortcuts. Off while an overlay of this modal is open so
  // a key pressed in the ship-to or customer search, or in the order modal
  // stacked on top, never switches the email underneath it.
  useDetailNavKeys(
    navigation,
    renderMode !== 'embedded' && !showShipToSearch && !showCustomerSearch && !showOrderDetailModal,
  );

  if (!email) return null;

  const isEmbedded = renderMode === 'embedded';
  const extrasCollapsed = isEmbedded && !embeddedExtrasOpen;
  const unread: boolean = isUnread ?? !!(email as any)?.isUnread;

  // In embedded mode, render without overlay wrapper
  const content = (
    <>
      <div className={isEmbedded ? styles.embeddedPanel : styles.modal} data-target-email-id={emailId ?? undefined}>
        {/* Header */}
        <div className={styles.header} data-section="email_header" data-target-email-id={emailId ?? undefined}>
          <div className={styles.headerInfo}>
            <div className={styles.subject}>
              {email.subject}
              {threadBadgeCount !== null && (
                <span className={styles.chainIndicator}>
                  {threadBadgeCount} messages
                </span>
              )}
            </div>
            <div className={styles.meta}>
              <span className={styles.metaLine}>From: {email.email}</span>
              <span className={styles.metaLine}>{email.time}</span>
              {email.lateSyncDays != null && (
                <span className={styles.metaLine}>
                  <span
                    className={styles.lateSyncBadge}
                    data-testid="email-modal-late-sync-badge"
                    title={lateSyncTitle(email.lateSyncDays, email.receivedAt, email.syncedAt)}
                  >
                    {formatLateSyncLabel(email.receivedAt, email.syncedAt)}
                  </span>
                </span>
              )}
              {(email as any).toAddress && (
                <span className={styles.metaLine}>To: {(email as any).toAddress}</span>
              )}
              {(email as any).ccAddresses && (
                <span className={styles.metaLine}>CC: {(email as any).ccAddresses}</span>
              )}
            </div>
          </div>
          <div className={styles.headerButtons} data-target-email-id={emailId ?? undefined}>
            {/* "Find the order" comes first on an email with no order
                (kanban 0c4b9133): after opening an email people went to
                /portal/orders 174 times in 90 days to open or search for
                the order. An email WITH an order never reaches this panel
                in modal mode (EmailViewModal below opens it beside the
                order), and the embedded pane already sits next to it. */}
            {!isEmbedded && emailId && !hasLinkedOrder && (
              <button
                type="button"
                className={styles.headerActionBtn}
                data-testid="email-find-order"
                data-action="email_find_order"
                onClick={() => setShowFindOrder(true)}
                title="Search the orders list for this email's customer without leaving the inbox"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                Find the order
              </button>
            )}
            {/* Embedded (split view) actions: the sidebar that normally holds
                Reply / Mark-as-read is hidden here, so they live in the header. */}
            {isEmbedded && emailId && (
              <div className={styles.embeddedActions} data-testid="embedded-email-actions">
                {onReplyClick && (
                  <>
                    <button
                      type="button"
                      className={`${styles.embeddedActionBtn} ${styles.embeddedActionPrimary}`}
                      data-testid="embedded-reply"
                      data-action="email_reply"
                      value="reply"
                      data-record-value="mode"
                      onClick={() => onReplyClick('reply')}
                      title="Reply to this email"
                    >
                      Reply
                    </button>
                    <button
                      type="button"
                      className={styles.embeddedActionBtn}
                      data-testid="embedded-reply-all"
                      data-action="email_reply"
                      value="reply_all"
                      data-record-value="mode"
                      onClick={() => onReplyClick('reply_all')}
                    >
                      Reply all
                    </button>
                  </>
                )}
                {unread && onMarkRead && (
                  <button
                    type="button"
                    className={styles.embeddedActionBtn}
                    data-testid="embedded-mark-read"
                    data-action="email_mark_read"
                    data-outcome="request"
                    onClick={() => onMarkRead(emailId)}
                    title="Clear the unread mark on this email"
                  >
                    Mark as read
                  </button>
                )}
                {!unread && onMarkUnread && (
                  <button
                    type="button"
                    className={styles.embeddedActionBtn}
                    data-testid="embedded-mark-unread"
                    data-action="email_mark_unread"
                    data-outcome="request"
                    onClick={() => onMarkUnread(emailId)}
                    title="Show this email as unread again"
                  >
                    Mark as unread
                  </button>
                )}
              </div>
            )}
            {navigation && !isEmbedded && (
              <DetailNavButtons navigation={navigation} actionPrefix="email" noun="email" />
            )}
            {/* Print dropdown — offers "Email Only" and "Email + Order" when order exists */}
            <div className={styles.printDropdownWrap} ref={printMenuRef}>
              <button
                className={styles.printBtn}
                onClick={() => {
                  const hasOrder = !!(
                    (email as any)?.hasOrder ||
                    (email as any)?.orderRecordStatus
                  );
                  if (hasOrder) {
                    setShowPrintMenu((prev) => !prev);
                  } else {
                    void handlePrintEmail();
                  }
                }}
                type="button"
                aria-label="Print"
                data-action="email_print"
                disabled={printLoading}
              >
                {printLoading ? (
                  <span className={styles.printSpinner} />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                )}
              </button>
              {showPrintMenu && (
                <div className={styles.printMenu}>
                  <button
                    className={styles.printMenuItem}
                    onClick={handlePrintEmail}
                    type="button"
                    data-action="email_print"
                    value="email_only"
                    data-record-value="mode"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    Print Email Only
                  </button>
                  <button
                    className={styles.printMenuItem}
                    onClick={handlePrintWithOrder}
                    type="button"
                    data-action="email_print"
                    value="email_and_order"
                    data-record-value="mode"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    Print Email + Order
                  </button>
                </div>
              )}
            </div>
            {/* Embedded (split view): the outer Split View bar owns the single
                close control — a second × per pane reads as window-in-window. */}
            {!isEmbedded && (
            <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close" data-action="email_close" data-outcome="close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            )}
          </div>
        </div>

        {/* Body - two columns */}
        <div className={styles.body} data-section="email_body" data-target-email-id={emailId ?? undefined}>
          {/* Left: Email content with tabbed attachments */}
          <div className={styles.emailContent}>
            {/* Content tabs: Email + one tab per attachment */}
            {(() => {
              const attachments: AttachmentMeta[] = (email as any).attachments ?? [];
              const hasAttachments = emailId && attachments.length > 0;
              const inlineOpen = isEmbedded ? openAttachmentInline : undefined;

              return (
                <>
                  {(hasAttachments || chainAttachmentTab) && (
                    <div className={styles.contentTabBar} data-section="email_content_tabs">
                      <button
                        className={`${styles.contentTab} ${activeContentTab === 'email' ? styles.contentTabActive : ''}`}
                        onClick={() => setActiveContentTab('email')}
                        type="button"
                        data-action="email_content_tab"
                        value="email"
                        data-record-value="tab"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        Email
                      </button>
                      {attachments.map((att) => {
                        const ext = att.filename.split('.').pop()?.toUpperCase() || '?';
                        const isPdf = att.mime_type === 'application/pdf' || (
                          att.mime_type === 'application/octet-stream' && att.filename.toLowerCase().endsWith('.pdf')
                        );
                        const isImg = att.mime_type.startsWith('image/');
                        return (
                          <button
                            key={att.filename}
                            className={`${styles.contentTab} ${activeContentTab === att.filename ? styles.contentTabActive : ''}`}
                            onClick={() => setActiveContentTab(att.filename)}
                            type="button"
                            title={att.filename}
                            data-action="email_attachment_open"
                            data-outcome="open"
                          >
                            <span className={`${styles.contentTabIcon} ${
                              isPdf ? styles.contentTabIconPdf :
                              isImg ? styles.contentTabIconImg :
                              styles.contentTabIconOther
                            }`}>
                              {ext.substring(0, 3)}
                            </span>
                            <span className={styles.contentTabLabel}>
                              {att.filename.length > 20 ? att.filename.substring(0, 17) + '...' : att.filename}
                            </span>
                          </button>
                        );
                      })}
                      {chainAttachmentTab && (
                        <button
                          className={`${styles.contentTab} ${activeContentTab === CHAIN_ATTACHMENT_TAB ? styles.contentTabActive : ''}`}
                          onClick={() => setActiveContentTab(CHAIN_ATTACHMENT_TAB)}
                          type="button"
                          title={`${chainAttachmentTab.attachment.filename} (earlier message in this thread)`}
                          data-testid="chain-attachment-tab"
                          data-action="email_attachment_open"
                          data-outcome="open"
                        >
                          <span className={`${styles.contentTabIcon} ${styles.contentTabIconOther}`}>
                            {(chainAttachmentTab.attachment.filename.split('.').pop() || '?').toUpperCase().substring(0, 3)}
                          </span>
                          <span className={styles.contentTabLabel}>
                            {chainAttachmentTab.attachment.filename.length > 20
                              ? chainAttachmentTab.attachment.filename.substring(0, 17) + '...'
                              : chainAttachmentTab.attachment.filename}
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Tab content: Email body */}
                  {activeContentTab === 'email' && (
                    <>
                      {isEmbedded && (
                        <button
                          type="button"
                          className={styles.embeddedExtrasToggle}
                          data-testid="embedded-extras-toggle"
                          data-action="email_extras_toggle"
                          onClick={() => setEmbeddedExtrasOpen((o) => !o)}
                          aria-expanded={!extrasCollapsed}
                        >
                          {extrasCollapsed ? 'Show AI notes & order details' : 'Hide AI notes & order details'}
                        </button>
                      )}

                      {!extrasCollapsed && email.hasStateRouting && (
                        <RoutingAlert email={email} />
                      )}

                      {!extrasCollapsed && (
                        <EmailDupPoBanner warning={(email as any).dupPoWarning} />
                      )}

                      {/* The Merlin edit proposal this email produced, shown
                          ON the email like a linked order (review 0vlvv8):
                          the same confirm card the order modal renders. When
                          present it supersedes the revision banner's "open
                          the order to review" indirection. */}
                      <EmailEditProposalSection
                        editProposal={(email as any).editProposal}
                        onOpenOrder={
                          emailId
                            ? (oid) => {
                                setOrderDetailEmailId(emailId);
                                setOrderDetailOrderId(oid);
                                setShowOrderDetailModal(true);
                              }
                            : undefined
                        }
                        onChanged={onEmailChanged}
                      />

                      {!extrasCollapsed && !(email as any).editProposal && (
                        <EmailNoDraftBanner
                          reason={(email as any).noDraftReason}
                          onOpenOrder={
                            emailId
                              ? (oid) => {
                                  setOrderDetailEmailId(emailId);
                                  setOrderDetailOrderId(oid);
                                  setShowOrderDetailModal(true);
                                }
                              : undefined
                          }
                        />
                      )}

                      {!extrasCollapsed && !isThreadDup && (((email as any).hasOrder || (email as any).orderRecordStatus || (email as any).classificationCategory === 'order') && emailId ? (
                        <div className={styles.orderSection}>
                          <OrderDetectedBadge
                            orderPreview={{
                              poNumber: (email as any).orderDetails?.poNumber,
                              products: (email as any).orderDetails?.products,
                              estValue: (email as any).orderDetails?.estValue,
                              confidence: (email as any).orderDetails?.confidence ?? (email as any).classificationConfidence,
                              confidenceBucket: (email as any).confidenceBucket ?? null,
                            }}
                            emailId={emailId}
                            orderExists={!!(email as any).hasOrder}
                            orderRecordStatus={(email as any).orderRecordStatus}
                            siblingOrders={((email as any).siblingOrders ?? []).map((s: any) => ({
                              id: s.id,
                              poNumber: s.po_number ?? s.poNumber,
                              status: s.status,
                              products: s.products,
                            }))}
                            onViewFullOrder={(eid) => {
                              setOrderDetailEmailId(eid);
                              setOrderDetailOrderId(null);
                              setShowOrderDetailModal(true);
                            }}
                            onViewOrderById={(oid) => {
                              setOrderDetailEmailId(emailId);
                              setOrderDetailOrderId(oid);
                              setShowOrderDetailModal(true);
                            }}
                          />
                          <ExtractionConcernsBanner
                            concerns={(email as any).extractionConcerns ?? (email as any).extraction_concerns ?? []}
                          />
                          <CreateOrderFromEmailButton
                            emailId={emailId}
                            hasExistingOrder
                            onOrderCreated={handleOrderCreatedFromEmail}
                          />
                        </div>
                      ) : emailId ? (
                        <div className={styles.orderSection}>
                          {((email as any).aiDraftState === 'drafting' ||
                            (email as any).aiDraftState === 'failed') && (
                            <AiDraftingNotice
                              state={(email as any).aiDraftState}
                            />
                          )}
                          <CreateOrderFromEmailButton
                            emailId={emailId}
                            onOrderCreated={handleOrderCreatedFromEmail}
                          />
                        </div>
                      ) : null)}

                      {/* AI Recommendation & Notification Feedback */}
                      {!extrasCollapsed && (email as any).decisionNotificationId && (
                        <NotificationFeedback
                          notificationId={(email as any).decisionNotificationId}
                          atAGlance={(email as any).atAGlance ?? null}
                          recommendedAction={(email as any).recommendedAction ?? null}
                          recommendedActionReasoning={(email as any).recommendedActionReasoning ?? null}
                          existingFeedback={(email as any).notificationFeedback ?? null}
                          existingFeedbackWorth={(email as any).notificationFeedbackWorth ?? null}
                          existingFeedbackActionUseful={(email as any).notificationFeedbackActionUseful ?? null}
                          classificationCategory={(email as any).classificationCategory ?? null}
                        />
                      )}

                      {/* Chain view toggle */}
                      {shouldShowThreadToggle(chainEmails.length) && (
                        <div className={styles.chainToggleBar}>
                          <button
                            className={`${styles.chainToggleBtn} ${showChain ? styles.chainToggleBtnActive : ''}`}
                            onClick={() => setShowChain(true)}
                            type="button"
                            data-action="email_thread_view"
                            value="full_thread"
                            data-record-value="view"
                          >
                            Full Thread ({chainEmails.length})
                          </button>
                          <button
                            className={`${styles.chainToggleBtn} ${!showChain ? styles.chainToggleBtnActive : ''}`}
                            onClick={() => setShowChain(false)}
                            type="button"
                            data-action="email_thread_view"
                            value="latest_only"
                            data-record-value="view"
                          >
                            Latest Only
                          </button>
                        </div>
                      )}

                      {/* Chain thread view */}
                      {shouldRenderThreadView(showChain, chainEmails.length) ? (
                        <EmailChainThread
                          emails={chainEmails}
                          autoExpandHead
                          onOpenAttachmentInline={inlineOpen}
                        />
                      ) : chainLoading ? (
                        <div className={styles.chainLoading}>Loading thread...</div>
                      ) : (
                        <>
                          <div className={styles.emailBodyHeader}>
                            <div className={styles.sectionTitle}>Email Body</div>
                            {effectiveBodyHtml && effectiveBody && (
                              <div className={styles.emailBodyToggle}>
                                <button
                                  className={`${styles.toggleBtn} ${emailBodyView === 'text' ? styles.toggleBtnActive : ''}`}
                                  onClick={() => setEmailBodyView('text')}
                                  type="button"
                                  data-action="email_body_view"
                                  value="text"
                                  data-record-value="view"
                                >
                                  Text
                                </button>
                                <button
                                  className={`${styles.toggleBtn} ${emailBodyView === 'html' ? styles.toggleBtnActive : ''}`}
                                  onClick={() => setEmailBodyView('html')}
                                  type="button"
                                  data-action="email_body_view"
                                  value="html"
                                  data-record-value="view"
                                >
                                  HTML
                                </button>
                              </div>
                            )}
                          </div>
                          {bodyLoading ? (
                            <div className={styles.emailBodyText} style={{ opacity: 0.5 }}>
                              Loading email body...
                            </div>
                          ) : emailBodyView === 'html' && effectiveBodyHtml ? (
                            <EmailHtmlViewer html={effectiveBodyHtml} />
                          ) : (
                            <div className={styles.emailBodyText}>
                              {effectiveBody || email.preview}
                            </div>
                          )}

                          {/* Attachment list (compact) — still shown in email tab for quick access */}
                          {hasAttachments && (
                            <AttachmentList
                              attachments={attachments}
                              emailId={emailId!}
                              onOpenInline={inlineOpen}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Tab content: Individual attachment preview */}
                  {activeContentTab === CHAIN_ATTACHMENT_TAB && chainAttachmentTab && (
                    <AttachmentTab
                      attachment={chainAttachmentTab.attachment}
                      emailId={chainAttachmentTab.emailId}
                    />
                  )}
                  {activeContentTab !== 'email' && activeContentTab !== CHAIN_ATTACHMENT_TAB && emailId && (() => {
                    const att = attachments.find((a) => a.filename === activeContentTab);
                    if (!att) return null;
                    return <AttachmentTab attachment={att} emailId={emailId} />;
                  })()}
                </>
              );
            })()}
          </div>

          {/* Right: Sidebar with metadata & actions (hidden in embedded mode) */}
          {!isEmbedded && <div className={styles.sidebar} data-section="email_sidebar" data-target-email-id={emailId ?? undefined}>
            {/* Customer Info */}
            <div className={styles.section}>
              <div className={styles.sectionTitleRow}>
                <div className={styles.sectionTitle}>Customer Info</div>
                {emailId && !showCustomerSearch && (
                  <button
                    type="button"
                    className={styles.editBtn}
                    onClick={() => setShowCustomerSearch(true)}
                    data-action="email_change_customer"
                    data-outcome="open"
                    title="Change customer"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                )}
              </div>
              <div className={styles.customerInfo}>
                {(() => {
                  const rawCust = customerOverride || (email as any).customer;
                  // Normalize: customerOverride uses camelCase keys,
                  // API response uses snake_case. Merge both so downstream
                  // checks work regardless of source.
                  const cust = rawCust ? {
                    ...rawCust,
                    customerNo: rawCust.customerNo || rawCust.customer_no,
                    isManualOverride: rawCust.isManualOverride ?? rawCust.is_manual_override ?? false,
                    lastOrder: rawCust.lastOrder || rawCust.last_order,
                    lastOrderValue: rawCust.lastOrderValue || rawCust.last_order_value,
                  } : null;
                  const isOverride = !!cust?.isManualOverride;
                  const isUnmapped = !isOverride && !cust?.customerNo;
                  return (
                    <>
                      {isUnmapped && (
                        <div className={styles.unmappedBanner}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          Potential New Customer?
                        </div>
                      )}
                      <div className={styles.customerName}>
                        {(cust?.slug || cust?.customerNo) ? (
                          <a
                            href={`/portal/customers/${cust.slug || cust.customerNo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.customerLink}
                            data-action="email_customer_open"
                            data-target-customer-no={cust.customerNo || undefined}
                          >
                            {cust?.name || email.from}
                            <svg className={styles.externalIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        ) : (
                          cust?.name || email.from
                        )}
                        {isOverride && (
                          <span className={styles.overrideBadge}>Manual</span>
                        )}
                      </div>
                      {orderDivergence && (
                        <div
                          className={styles.divergenceBadge}
                          data-testid="order-divergence-badge"
                          title="The linked order still has a different customer than this email. Reassign the customer and choose to update the order to fix it."
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                          Linked order
                          {orderDivergence.po ? ` (PO ${orderDivergence.po})` : ''}{' '}
                          still has {orderDivergence.orderCustomer}
                        </div>
                      )}
                      {cust?.type && (
                        <div className={styles.customerDetail}>{cust.type}</div>
                      )}
                      {(cust?.account || cust?.salesperson) && (
                        <div className={styles.customerDetail}>
                          Sales Rep: {cust.account || cust.salesperson}
                        </div>
                      )}
                      {cust?.lastOrder && (
                        <div className={styles.customerDetail}>
                          Last Order: {cust.lastOrder} - {cust.lastOrderValue}
                        </div>
                      )}
                      {cust?.domain && (
                        <div className={styles.customerDetail}>Domain: {cust.domain}</div>
                      )}
                      {cust?.note && (
                        <div className={styles.customerNote}>{cust.note}</div>
                      )}
                      {isOverride && emailId && (
                        <button
                          type="button"
                          className={styles.clearOverrideBtn}
                          onClick={handleClearOverride}
                          disabled={clearingOverride}
                        >
                          {clearingOverride ? 'Clearing...' : 'Clear override'}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              {showCustomerSearch && emailId && (
                <CustomerSearch
                  emailId={emailId}
                  senderName={email.from}
                  senderEmail={email.email}
                  onOverride={handleCustomerOverride}
                  onCancel={() => setShowCustomerSearch(false)}
                  onToast={onToast}
                />
              )}
              {/* Propose-new-customer / ship-to card (structured AI proposal) */}
              {(email as any).proposals && emailId && (
                <div style={{ marginTop: 8 }}>
                  <ProposalCard
                    proposals={(email as any).proposals}
                    emailId={emailId}
                    orderId={linkedDraftOrder?.id}
                    orderCustomerNo={linkedDraftOrder?.customerNo}
                    onShipToCreated={(addr) => {
                      // Bind the fresh code + address onto the linked draft —
                      // the same PATCH OrderDetailModal applies.
                      if (!linkedDraftOrder) return;
                      void portalOrderService
                        .updateOrder(linkedDraftOrder.id, {
                          ship_to_address: {
                            ship_to_code: addr.ship_to_code,
                            ship_to_name: addr.ship_to_name,
                            address1: addr.address1,
                            address2: addr.address2,
                            city: addr.city,
                            state: addr.state,
                            zip_code: addr.zip_code,
                          },
                        })
                        .then(() => {
                          onToast?.(
                            `Ship-to ${addr.ship_to_code} created & set on the draft`,
                            'success',
                          );
                          onEmailChanged?.();
                        })
                        .catch(() =>
                          onToast?.('Ship-to created but failed to bind to the draft', 'error'),
                        );
                    }}
                    onChanged={handleCustomerOverride}
                    onToast={onToast}
                  />
                </div>
              )}
            </div>

            {/* Ship-To Address — editable search section */}
            <div className={styles.section}>
              <div className={styles.sectionTitleRow}>
                <div className={styles.sectionTitle}>Ship-To Address</div>
                {emailId && !showShipToSearch && (
                  <button
                    type="button"
                    className={styles.editBtn}
                    onClick={() => setShowShipToSearch(true)}
                    data-action="email_change_ship_to"
                    data-outcome="open"
                    title="Search ship-to address"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                )}
              </div>
              {(() => {
                // Prefer user-selected ship-to, fall back to extracted/order data
                if (selectedShipTo) {
                  return (
                    <div className={styles.customerInfo}>
                      <div className={styles.customerName}>{selectedShipTo.ship_to_name || selectedShipTo.ship_to_code}</div>
                      <div className={styles.customerDetail}>
                        {[selectedShipTo.address1, selectedShipTo.city, selectedShipTo.state, selectedShipTo.zip_code].filter(Boolean).join(', ')}
                      </div>
                      {selectedShipTo.warehouse_code && (
                        <div className={styles.customerDetail}>
                          Warehouse: {selectedShipTo.warehouse_code}
                        </div>
                      )}
                      {selectedShipTo.customer_name && (
                        <div className={styles.customerDetail}>
                          Customer: {selectedShipTo.customer_name} ({selectedShipTo.customer_no})
                        </div>
                      )}
                      {shipToSaving && (
                        <div className={styles.customerDetail} style={{ color: 'var(--primary)' }}>
                          Saving...
                        </div>
                      )}
                    </div>
                  );
                }
                const shipTo = (email as any)?.orderDetails?.shipToAddress
                  || (email as any)?.shipping;
                if (!shipTo) {
                  return (
                    <div className={styles.customerInfo}>
                      <div className={styles.customerDetail} style={{ fontStyle: 'italic' }}>
                        No ship-to address set
                      </div>
                    </div>
                  );
                }
                const addr = shipTo.ship_to_name || shipTo.address;
                const line = [
                  shipTo.address1 || shipTo.address,
                  shipTo.city,
                  shipTo.state,
                  shipTo.zip_code,
                ].filter(Boolean).join(', ');
                return (
                  <div className={styles.customerInfo}>
                    {addr && <div className={styles.customerName}>{addr}</div>}
                    {line && <div className={styles.customerDetail}>{line}</div>}
                    {shipTo.warehouse_code && (
                      <div className={styles.customerDetail}>
                        Warehouse: {shipTo.warehouse_code}
                      </div>
                    )}
                  </div>
                );
              })()}
              {showShipToSearch && emailId && (
                <ShipToSearch
                  customerNo={
                    customerOverride?.customerNo
                    || (email as any)?.customer?.customer_no
                    || null
                  }
                  customerName={
                    customerOverride?.name
                    || (email as any)?.customer?.name
                    || undefined
                  }
                  onSelect={handleShipToSelect}
                  onCustomerMismatch={handleShipToMismatch}
                  onCancel={() => setShowShipToSearch(false)}
                />
              )}
            </div>

            {/* Actions */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Actions</div>
              <div className={styles.actions} data-section="email_actions">
                <button
                  className={`${styles.actionBtn} ${styles.primary}`}
                  onClick={() => onReplyClick?.('reply')}
                  type="button"
                  data-action="email_reply"
                  value="reply"
                  data-record-value="mode"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 17 4 12 9 7" />
                    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                  </svg>
                  Reply
                </button>
                <button
                  className={styles.actionBtn}
                  onClick={() => onReplyClick?.('reply_all')}
                  type="button"
                  data-action="email_reply"
                  value="reply_all"
                  data-record-value="mode"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="7 17 2 12 7 7" />
                    <polyline points="12 17 7 12 12 7" />
                    <path d="M22 18v-2a4 4 0 0 0-4-4H7" />
                  </svg>
                  Reply All
                </button>
                <button
                  className={styles.actionBtn}
                  onClick={() => onReplyClick?.('forward')}
                  type="button"
                  data-action="email_reply"
                  value="forward"
                  data-record-value="mode"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 17 20 12 15 7" />
                    <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                  </svg>
                  Forward
                </button>
                {emailId && unread && onMarkRead && (
                  <button
                    className={`${styles.actionBtn} ${styles.markUnread}`}
                    onClick={() => onMarkRead(emailId)}
                    type="button"
                    data-testid="sidebar-mark-read"
                    data-action="email_mark_read"
                    data-outcome="request"
                    title="Clear the unread mark on this email"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22 6 12 13 2 6" />
                    </svg>
                    Mark as Read
                  </button>
                )}
                {emailId && !unread && onMarkUnread && (
                  <button
                    className={`${styles.actionBtn} ${styles.markUnread}`}
                    onClick={() => {
                      onMarkUnread(emailId);
                      onClose();
                    }}
                    type="button"
                    data-testid="sidebar-mark-unread"
                    data-action="email_mark_unread"
                    data-outcome="request"
                    title="Mark as unread — email will appear bold in your inbox"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22 6 12 13 2 6" />
                    </svg>
                    Mark as Unread
                  </button>
                )}
                {emailId && canResolveActionItem(email) && (
                  <button
                    className={`${styles.actionBtn} ${isDismissed ? styles.resolvedBtn : styles.success}`}
                    onClick={handleToggleDismiss}
                    disabled={dismissing}
                    type="button"
                    data-action={isDismissed ? 'email_unresolve' : 'email_resolve'}
                    data-outcome="request"
                    title={isDismissed ? 'Undo resolve — restore this action item' : 'Mark as resolved — action item handled'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {isDismissed ? (
                        <>
                          <path d="M1 4v6h6" />
                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                        </>
                      ) : (
                        <polyline points="20 6 9 17 4 12" />
                      )}
                    </svg>
                    {dismissing
                      ? (isDismissed ? 'Restoring...' : 'Resolving...')
                      : (isDismissed ? 'Undo Resolve' : 'Mark as Resolved')}
                  </button>
                )}
                {emailId && (
                  <button
                    className={`${styles.actionBtn} ${styles.danger}`}
                    onClick={handleMarkAsTrash}
                    disabled={trashing}
                    type="button"
                    data-action="email_trash"
                    data-outcome="request"
                    title="Mark as trash — hides from unmapped view"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    {trashing ? 'Trashing...' : 'Trash'}
                  </button>
                )}
                {isAdmin && emailId && (
                  <button
                    className={styles.actionBtn}
                    onClick={() => navigate(`/email-monitor-decisions?emailId=${emailId}`)}
                    type="button"
                    data-action="email_view_agent_run"
                    data-outcome="navigate"
                    title="View agent decision for this email (admin only)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    View Agent Run
                  </button>
                )}
              </div>
            </div>
          </div>}
        </div>
      </div>

      {/* Split-screen view (opened from "View Full Order" button) — only in modal mode */}
      {!isEmbedded && showOrderDetailModal && orderDetailEmailId && (
        <SplitScreenModal
          email={email}
          emailId={orderDetailEmailId}
          orderId={orderDetailOrderId}
          onClose={() => {
            setShowOrderDetailModal(false);
            setOrderDetailEmailId(null);
            setOrderDetailOrderId(null);
          }}
          onOrderAction={onOrderAction}
          onReplyClick={onReplyClick}
          onEmailChanged={onEmailChanged}
          onToast={onToast}
        />
      )}

      {/* "Find the order": the Orders search, in place, for an email with no
          linked order. A picked row opens in the split view above. */}
      {!isEmbedded && showFindOrder && emailId && (
        <FindOrderModal
          emailId={emailId}
          initialTerm={findOrderSearch.term}
          customerNo={findOrderSearch.customerNo}
          onClose={() => setShowFindOrder(false)}
          onOpenOrder={openFoundOrder}
        />
      )}

      {trashGuard && (
        <TrashGuardModal
          guarded={trashGuard}
          totalSelected={1}
          busy={trashing}
          onCancel={() => setTrashGuard(null)}
          onTrashAnyway={() => {
            setTrashGuard(null);
            void performTrash(true);
          }}
        />
      )}
      {printJob && <EmailPrintDocument job={printJob} onReady={handlePrintReady} />}
    </>
  );

  if (isEmbedded) {
    return content;
  }

  // No click-off close (kanban 2c097936): a click outside the panel while
  // reading leaves the email open. The header's close button and Escape
  // close it.
  return (
    <div className={styles.overlay}>
      {content}
    </div>
  );
};

/**
 * Opening an email that has an order shows the order beside it, from every
 * list that mounts this component (inbox, all emails, deep links from
 * notifications), the same side-by-side the Orders page opens for an email
 * order. The rule lives here, once, so no page decides it: an email with an
 * order renders the split view directly (closing it returns to the list,
 * Prev / Next step the list); an email without one gets the plain panel
 * with "Find the order"; the embedded pane inside a split view is always the
 * plain panel. Recorded as view/email_open_order source 'auto' so the usage
 * graph can tell an automatic side-by-side open from a chosen one. The row
 * is written when the order pane has resolved the order from the email, so it
 * carries BOTH the email id and the order id (kanban 1136a376: the trace from
 * a purchase-order email to the order made from it is keyed on the pair);
 * order_id is null when the email turned out to have no order yet.
 */
const EmailViewModal: React.FC<EmailViewModalProps> = (props) => {
  const {
    email,
    emailId,
    renderMode = 'modal',
    navigation,
    onClose,
    onOrderAction,
    onReplyClick,
    onMarkRead,
    onMarkUnread,
    isUnread,
    onEmailChanged,
    onToast,
  } = props;
  const opensBesideOrder =
    renderMode !== 'embedded' && !!emailId && emailHasLinkedOrder(email as any);

  // One row per email opened, even when the order pane resolves twice
  // (StrictMode in dev) or the email row re-renders.
  const recordedForRef = useRef<string | null>(null);
  const recordAutoOpen = useCallback((orderId: string | null) => {
    if (!emailId || recordedForRef.current === emailId) return;
    recordedForRef.current = emailId;
    trackEvent('view', 'email_open_order', {
      email_id: emailId,
      order_id: orderId,
      source: 'auto',
    });
  }, [emailId]);

  if (!opensBesideOrder || !emailId) return <EmailViewPanel {...props} />;
  return (
    <SplitScreenModal
      email={email}
      emailId={emailId}
      orderId={null}
      onClose={onClose}
      closeAction="email_close"
      onOrderResolved={recordAutoOpen}
      navigation={navigation}
      onOrderAction={onOrderAction}
      onReplyClick={onReplyClick}
      onMarkRead={onMarkRead}
      onMarkUnread={onMarkUnread}
      isUnread={isUnread}
      onEmailChanged={onEmailChanged}
      onToast={onToast}
    />
  );
};

export default EmailViewModal;
